/**
 * US federal holidays — minimal, hard-coded calendar for the MVP.
 *
 * Drives the "Fill as PTO" banner on WeekGridView so a user on leave
 * doesn't have to type 8h onto a PTO row by hand. Future work
 * (multi-state, SCA wage-determination metadata, CBA-specific holidays,
 * per-employee balances) lands when org-config tables exist.
 *
 * Dates are ISO yyyy-MM-dd; comparisons are string-lex which works for
 * this format. UTC-naive — holidays are observed on the calendar date
 * regardless of the employee's timezone.
 *
 * Ported from Clients/Mach12/timesheet/src/lib/holidays.ts.
 */

export type HolidayJurisdiction = 'US' | 'CA' | 'NY' | 'MA' | 'CO' | 'TX'

export interface Holiday {
  date: string
  name: string
  /** True when the listed date is a legal-holiday-in-lieu-of (the actual fell on a weekend). */
  observed?: boolean
  /** Defaults to 'US' (federal). State holidays use their two-letter code. */
  jurisdiction?: HolidayJurisdiction
}

const H_2026: readonly Holiday[] = [
  { date: '2026-01-01', name: "New Year's Day" },
  { date: '2026-01-19', name: 'Martin Luther King Jr. Day' },
  { date: '2026-02-16', name: "Presidents' Day" },
  { date: '2026-05-25', name: 'Memorial Day' },
  { date: '2026-06-19', name: 'Juneteenth' },
  { date: '2026-07-03', name: 'Independence Day', observed: true }, // July 4 falls Saturday
  { date: '2026-09-07', name: 'Labor Day' },
  { date: '2026-10-12', name: 'Columbus Day' },
  { date: '2026-11-11', name: 'Veterans Day' },
  { date: '2026-11-26', name: 'Thanksgiving' },
  { date: '2026-12-25', name: 'Christmas Day' },
]

const H_2027: readonly Holiday[] = [
  { date: '2027-01-01', name: "New Year's Day" },
  { date: '2027-01-18', name: 'Martin Luther King Jr. Day' },
  { date: '2027-02-15', name: "Presidents' Day" },
  { date: '2027-05-31', name: 'Memorial Day' },
  { date: '2027-06-18', name: 'Juneteenth', observed: true }, // June 19 falls Saturday
  { date: '2027-07-05', name: 'Independence Day', observed: true }, // July 4 falls Sunday
  { date: '2027-09-06', name: 'Labor Day' },
  { date: '2027-10-11', name: 'Columbus Day' },
  { date: '2027-11-11', name: 'Veterans Day' },
  { date: '2027-11-25', name: 'Thanksgiving' },
  { date: '2027-12-24', name: 'Christmas Day', observed: true }, // Dec 25 falls Saturday
]

export const FEDERAL_HOLIDAYS: readonly Holiday[] = [...H_2026, ...H_2027]

// ─── State-specific holidays (additive — federal holidays apply too) ─────────

const STATE_HOLIDAYS_2026: Record<Exclude<HolidayJurisdiction, 'US'>, Holiday[]> = {
  CA: [
    { date: '2026-03-31', name: 'Cesar Chavez Day', jurisdiction: 'CA' },
    { date: '2026-11-27', name: 'Day After Thanksgiving', jurisdiction: 'CA' },
  ],
  NY: [
    { date: '2026-11-03', name: 'Election Day (NY)', jurisdiction: 'NY' },
    { date: '2026-11-27', name: 'Day After Thanksgiving (NY)', jurisdiction: 'NY' },
  ],
  MA: [
    { date: '2026-04-20', name: "Patriots' Day", jurisdiction: 'MA' },
    { date: '2026-11-27', name: 'Day After Thanksgiving (MA)', jurisdiction: 'MA' },
  ],
  CO: [
    { date: '2026-03-31', name: 'Cesar Chavez Day (CO, optional)', jurisdiction: 'CO' },
  ],
  TX: [
    { date: '2026-01-19', name: 'Confederate Heroes Day (TX)', jurisdiction: 'TX' },
    { date: '2026-03-02', name: 'Texas Independence Day', jurisdiction: 'TX' },
    { date: '2026-04-21', name: 'San Jacinto Day (TX)', jurisdiction: 'TX' },
    { date: '2026-06-19', name: 'Emancipation Day (TX)', jurisdiction: 'TX' },
  ],
}

const STATE_HOLIDAYS_2027: Record<Exclude<HolidayJurisdiction, 'US'>, Holiday[]> = {
  CA: [
    { date: '2027-03-31', name: 'Cesar Chavez Day', jurisdiction: 'CA' },
    { date: '2027-11-26', name: 'Day After Thanksgiving', jurisdiction: 'CA' },
  ],
  NY: [
    { date: '2027-11-02', name: 'Election Day (NY)', jurisdiction: 'NY' },
    { date: '2027-11-26', name: 'Day After Thanksgiving (NY)', jurisdiction: 'NY' },
  ],
  MA: [
    { date: '2027-04-19', name: "Patriots' Day", jurisdiction: 'MA' },
    { date: '2027-11-26', name: 'Day After Thanksgiving (MA)', jurisdiction: 'MA' },
  ],
  CO: [
    { date: '2027-03-31', name: 'Cesar Chavez Day (CO, optional)', jurisdiction: 'CO' },
  ],
  TX: [
    { date: '2027-01-19', name: 'Confederate Heroes Day (TX)', jurisdiction: 'TX' },
    { date: '2027-03-02', name: 'Texas Independence Day', jurisdiction: 'TX' },
    { date: '2027-04-21', name: 'San Jacinto Day (TX)', jurisdiction: 'TX' },
    { date: '2027-06-19', name: 'Emancipation Day (TX)', jurisdiction: 'TX' },
  ],
}

export const STATE_HOLIDAYS: Record<Exclude<HolidayJurisdiction, 'US'>, readonly Holiday[]> = {
  CA: [...STATE_HOLIDAYS_2026.CA, ...STATE_HOLIDAYS_2027.CA],
  NY: [...STATE_HOLIDAYS_2026.NY, ...STATE_HOLIDAYS_2027.NY],
  MA: [...STATE_HOLIDAYS_2026.MA, ...STATE_HOLIDAYS_2027.MA],
  CO: [...STATE_HOLIDAYS_2026.CO, ...STATE_HOLIDAYS_2027.CO],
  TX: [...STATE_HOLIDAYS_2026.TX, ...STATE_HOLIDAYS_2027.TX],
}

// ─── SAP holiday overlay (live-SAP forks only) ───────────────────────────────
// A live-SAP fork primes these from sap_holiday_calendar (migration 00425) via
// setSapHolidayOverlay, so the customer's own calendar overlays the built-in
// federal/state list everywhere holidays are read (isHoliday / holidaysInRange /
// holidayFor, and weekTarget through them). EMPTY in the demo — the fast-path
// below returns the base list untouched, so demo behavior is unchanged.
let sapOverlay: readonly Holiday[] = []
let sapWorkdayDates: ReadonlySet<string> = new Set()

/**
 * Overlay the given SAP-landed holidays onto the built-in calendar. `holidays`
 * add or override federal/state dates; `workdayDates` are dates SAP marks as
 * working (a special/half day) and are removed from the holiday set. Pass empty
 * arrays to clear the overlay (the demo/default state).
 */
export function setSapHolidayOverlay(holidays: readonly Holiday[], workdayDates: readonly string[] = []): void {
  sapOverlay = holidays
  sapWorkdayDates = new Set(workdayDates)
}

function applySapOverlay(base: Holiday[]): Holiday[] {
  if (!sapOverlay.length && !sapWorkdayDates.size) return base // demo fast-path: unchanged
  const byDate = new Map<string, Holiday>()
  for (const h of base) byDate.set(h.date, h)
  for (const d of sapWorkdayDates) byDate.delete(d)
  for (const h of sapOverlay) byDate.set(h.date, h) // SAP overrides / adds
  return [...byDate.values()]
}

/**
 * Returns holidays that apply to the given jurisdictions. Always includes
 * federal, plus any live-SAP overlay. Pass an empty array (or omit) for
 * federal-only.
 */
function holidaysForJurisdictions(jurisdictions: ReadonlyArray<HolidayJurisdiction>): Holiday[] {
  // Federal first, then state additions; dedupe by date so an overlapping
  // state observance (e.g. TX Emancipation Day on Juneteenth) doesn't double up.
  const federalOnly = !jurisdictions.length || (jurisdictions.length === 1 && jurisdictions[0] === 'US')
  const seen = new Set<string>()
  const out: Holiday[] = []
  for (const h of FEDERAL_HOLIDAYS) {
    if (seen.has(h.date)) continue
    seen.add(h.date)
    out.push(h)
  }
  if (!federalOnly) {
    for (const j of jurisdictions) {
      if (j === 'US') continue
      for (const h of STATE_HOLIDAYS[j]) {
        if (seen.has(h.date)) continue
        seen.add(h.date)
        out.push(h)
      }
    }
  }
  return applySapOverlay(out)
}

/**
 * Inclusive range. Federal holidays always included; pass `jurisdictions`
 * to additively include state holidays.
 */
export function holidaysInRange(
  startIso: string,
  endIso: string,
  jurisdictions: ReadonlyArray<HolidayJurisdiction> = [],
): Holiday[] {
  return holidaysForJurisdictions(jurisdictions)
    .filter((h) => h.date >= startIso && h.date <= endIso)
    .sort((a, b) => a.date.localeCompare(b.date))
}

export function isHoliday(
  dateIso: string,
  jurisdictions: ReadonlyArray<HolidayJurisdiction> = [],
): boolean {
  return holidaysForJurisdictions(jurisdictions).some((h) => h.date === dateIso)
}

export function holidayFor(
  dateIso: string,
  jurisdictions: ReadonlyArray<HolidayJurisdiction> = [],
): Holiday | undefined {
  return holidaysForJurisdictions(jurisdictions).find((h) => h.date === dateIso)
}
